import { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen, CheckCircle2, Loader2 } from "lucide-react";
import { track } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PERIMENOPAUSE_GUIDE = {
  guide: "perimenopause",
  cover: "/images/guide-cover.webp",
  coverAlt: "Cover of the All-In Precision Wellness Perimenopause Guide",
  title: "Your body is changing. Let\u2019s make sense of it.",
  description:
    "Get Dr. Crosson\u2019s free Perimenopause Guide: understand the transition, spot your patterns, and prepare for a better care conversation.",
  button: "Get the Free Guide",
  fileName: "All-In-Perimenopause-Guide.pdf",
  path: "/guides/All-In-Perimenopause-Guide.pdf",
  successNoun: "guide",
};

export const GuideDialog = ({ open, onOpenChange, variant, kit }) => {
  const content = kit || PERIMENOPAUSE_GUIDE;
  const [form, setForm] = useState({ first: "", last: "", email: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.first.trim()) errs.first = "Please enter your first name.";
    if (!form.last.trim()) errs.last = "Please enter your last name.";
    if (!EMAIL_RE.test(form.email.trim())) errs.email = "Please enter a valid email address.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("submitting");
    try {
      const res = await axios.post(`${API}/guide-downloads`, {
        first_name: form.first.trim(),
        last_name: form.last.trim(),
        email: form.email.trim(),
        guide: content.guide,
        company: e.target.elements.company?.value || null,
      });
      track(variant, "guide_download");
      const a = document.createElement("a");
      a.href = res.data.download_url;
      a.download = content.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="guide-dialog" className="max-w-md p-0 overflow-hidden bg-white">
        <div className="bg-[var(--brand-navy)] px-6 py-6 text-white flex items-center gap-5">
          <div className="guide-book w-24 shrink-0 hidden sm:block">
            <div className="guide-book-inner">
              <img src={content.cover} alt={content.coverAlt} />
            </div>
          </div>
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-gold)]">
              <BookOpen className="h-4 w-4" aria-hidden="true" /> Free download
            </p>
            <DialogHeader className="text-left mt-2 space-y-1">
              <DialogTitle className="font-serif-brand text-2xl font-medium text-white">
                {content.title}
              </DialogTitle>
              <DialogDescription className="text-white/80 text-base">
                {content.description}
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>
        <div className="px-6 pb-6 pt-5">
          {status === "success" ? (
            <div data-testid="guide-success-message" className="py-4 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto text-[var(--brand-gold)]" aria-hidden="true" />
              <h3 className="mt-3 font-serif-brand text-xl font-medium text-[var(--brand-navy)]">
                Your download has started.
              </h3>
              <p className="mt-2 text-base leading-relaxed">
                We also emailed a copy to you so it&rsquo;s easy to find later. When you&rsquo;re
                ready, call (484) 544-3345 to talk about your visit.
              </p>
              <a
                data-testid="guide-direct-link"
                href={content.path}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-block text-sm font-medium text-[var(--brand-navy)] underline decoration-[var(--brand-gold)] decoration-2 underline-offset-4"
              >
                Didn&rsquo;t start? Open the guide here.
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" noValidate>
              <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="gd-first">First name *</Label>
                  <Input id="gd-first" data-testid="guide-first-name-input" value={form.first} onChange={setField("first")} autoComplete="given-name" className="mt-1.5" />
                  {errors.first && <p className="mt-1 text-sm text-red-700">{errors.first}</p>}
                </div>
                <div>
                  <Label htmlFor="gd-last">Last name *</Label>
                  <Input id="gd-last" data-testid="guide-last-name-input" value={form.last} onChange={setField("last")} autoComplete="family-name" className="mt-1.5" />
                  {errors.last && <p className="mt-1 text-sm text-red-700">{errors.last}</p>}
                </div>
              </div>
              <div>
                <Label htmlFor="gd-email">Email *</Label>
                <Input id="gd-email" data-testid="guide-email-input" type="email" inputMode="email" value={form.email} onChange={setField("email")} autoComplete="email" className="mt-1.5" />
                {errors.email && <p className="mt-1 text-sm text-red-700">{errors.email}</p>}
              </div>
              {status === "error" && (
                <p data-testid="guide-error-message" className="text-sm text-red-700 bg-red-50 border border-red-200 p-3">
                  Something went wrong. Please try again, or call (484) 544-3345.
                </p>
              )}
              <button
                data-testid="guide-submit-button"
                type="submit"
                disabled={status === "submitting"}
                className="w-full inline-flex items-center justify-center gap-2 bg-[var(--brand-navy)] text-white text-lg font-semibold py-3.5 hover:bg-[var(--brand-navy-deep)] transition-colors disabled:opacity-60"
              >
                {status === "submitting" && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
                {content.button}
              </button>
              <p className="text-xs leading-relaxed text-[var(--brand-muted)]">
                We&rsquo;ll email your copy right away and the practice team is notified of your
                request. No spam, and your details are never shared with advertising platforms.
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
